import { subscribeKey } from 'valtio/utils';

import { Deferrable } from '@canvas/deferrable';
import { NoopContentAPI } from '@canvas/frame-api-noop';
import {
  type ContainerHandler,
  type OnboardingStartHandler,
  type RawContentAPI,
  type RawFrameToken,
  type RawTokenHandler,
  type WhiteboardSessionData,
  type ConfluenceSidebarState,
  type MoreActions,
  type ContentTypesFlagProps,
  UserTypes,
  type WhiteboardQueryParams,
} from '@canvas/frame-api-types';
import { BG_DOTTED, type BackgroundPreference } from '@canvas/model/preferences';
import { unlockAllPaywallsResponseMock } from '@canvas/paywall-test-utils';
import { currentRightSidebarPanel } from '@canvas/sidebar-panel-state';
import { getQueryParams } from '@canvas/utils';

import { currentDevelopmentUIState } from './dev-ui-state';
import { addFlag, removeFlag, getCurrentFlags } from './dev-ui-state-flags';
import { getTokenFromLocalServer, getTokenFromSite } from './get-access-token';
import { getBoardIdForLocalDevelopment } from './get-board-id';
import { getSpaceId } from './get-space-id';
import { localPlatformFeatureFlagClient } from './platform-feature-flags-local';

const developmentAccountId =
  process.env.VITE_LOCAL_ACCOUNT_ID ?? process.env.VITE_CANVAS_EAP_ACCOUNT_ID ?? '';
const developmentCloudId = process.env.VITE_CANVAS_EAP_CLOUD_ID ?? '';
const developmentActivationId = process.env.VITE_CANVAS_EAP_ACTIVATION_ID ?? '';

const developmentSessionData: WhiteboardSessionData = {
  accountId: developmentAccountId,
  tenantId: developmentCloudId,
  activationId: developmentActivationId,
  publicLinkUserType: UserTypes.CONFLUENCE_USER,
};

const requestFrameToken = (boardId: string): Promise<Awaited<RawFrameToken>> => {
  const localAsapToken = process.env.VITE_LOCAL_ASAP_TOKEN;
  const localSpaceId = process.env.VITE_LOCAL_SPACE_ID;

  const isLocalDevLoop = localAsapToken && localSpaceId;

  if (globalThis.ATLASSIAN_CANVAS?.frameToken !== undefined) {
    return Promise.resolve(globalThis.ATLASSIAN_CANVAS.frameToken as RawFrameToken);
  }

  return isLocalDevLoop
    ? getTokenFromLocalServer({
        boardId,
        localSpaceId,
        localAsapToken,
        developmentCloudId,
      })
    : getTokenFromSite({
        boardId,
        developmentSpaceId: getSpaceId(),
        developmentCloudId,
      });
};

export class ContentAPILocal extends NoopContentAPI implements RawContentAPI {
  public sessionData = developmentSessionData;
  private sidebarStateCallbacks: Array<(state: ConfluenceSidebarState) => void> = [];

  private fetching: Promise<RawFrameToken> | undefined;

  protected getQueryParams(query: WhiteboardQueryParams | undefined): {
    [k: string]: any;
  } {
    const windowParams = getQueryParams();

    return {
      ...Object.fromEntries(windowParams),
      ...super.getQueryParams(query),
    };
  }

  public onTokenInit = async (handler: RawTokenHandler) => {
    const token = await this.getToken();
    handler(token);
  };

  public onContainerInit = (handler: ContainerHandler) => {
    handler({
      key: process.env.VITE_CANVAS_EAP_CONTAINER ?? '',
      name: process.env.VITE_CANVAS_EAP_CONTAINER_NAME ?? '',
      source: 'Confluence',
    });
  };

  public getToken = async (): Promise<RawFrameToken> => {
    const boardId = getBoardIdForLocalDevelopment();
    if (!boardId) {
      return {
        container: '',
        board: '',
        accessToken: '',
        mediaToken: '',
        mediaBaseUrl: '',
        mediaCollectionId: '',
        features: unlockAllPaywallsResponseMock,
      };
    }

    if (this.fetching) {
      return this.fetching;
    }

    const tokenPromise = requestFrameToken(boardId);

    this.fetching = tokenPromise;

    return await tokenPromise.then((data) => {
      this.fetching = undefined;

      if (data.mediaCollectionId) {
        currentDevelopmentUIState.contentId = data.mediaCollectionId;
      }

      return data;
    });
  };

  public showEditionUpgradeCTA = () => {
    const deferrable = new Deferrable<void>();

    currentRightSidebarPanel.panel = 'local-paywall-cta-panel';

    // Subscribe to next panel update, when it changes we know that this panel
    // was closed and can resolve our deferred promise
    const unsubscribe = subscribeKey(currentRightSidebarPanel, 'panel', () => {
      deferrable.resolve();

      unsubscribe();
    });

    return deferrable.get();
  };

  public closeEditionUpgradeCTA = () => {
    currentRightSidebarPanel.panel = 'none';
  };

  public registerStartOnboardingCallback = (cb: OnboardingStartHandler) => {
    (window as any).startOnboarding = cb;
  };

  public platformFeatureFlagResolver = (name: string) => {
    return localPlatformFeatureFlagClient?.checkGate(name) ?? false;
  };

  public getGridPreference = (): BackgroundPreference => BG_DOTTED;

  public updateMoreActions = (actions: MoreActions) => {
    currentDevelopmentUIState.moreActions = actions;
  };

  public showFlag = (props: ContentTypesFlagProps) => {
    const flagId = String(
      props.id || `flag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    );
    const flagProps = { ...props, id: flagId };
    addFlag(flagId, flagProps);

    if (props.close === 'auto') {
      setTimeout(() => {
        this.hideFlag(flagId);
      }, 8000); // 8 seconds like the databases implementation
    }
  };

  public hideFlag = (flagId: string) => {
    const currentFlags = getCurrentFlags();
    const flag = currentFlags.find((flag) => flag.flagId === flagId);
    if (flag) {
      flag.onClose?.();
      removeFlag(flagId);

      // Dispatch a custom event to notify UI components that a flag was hidden
      // This ensures the DevFlags component updates its state
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('canvas-flag-hidden', {
            detail: { flagId },
          })
        );
      }
    }
  };

  public getCurrentFlags = () => {
    return getCurrentFlags();
  };

  public confluenceAPI = {
    sidebar: {
      showCommentsPanel: () => {
        this.notifySidebarStateChange({ isOpen: true, panelId: 'commentsPanel' });
      },
      showDetailsPanel: () => {
        this.notifySidebarStateChange({ isOpen: true, panelId: 'detailsPanel' });
      },
      hideCommentsPanel: () => {
        this.notifySidebarStateChange({ isOpen: false, panelId: undefined });
      },
      hideDetailsPanel: () => {
        this.notifySidebarStateChange({ isOpen: false, panelId: undefined });
      },
      hideExternalDrawerPanels: () => {
        this.notifySidebarStateChange({ isOpen: false, panelId: undefined });
      },
      onConfluenceSidebarStateChange: (
        callback: (state: ConfluenceSidebarState) => void
      ) => {
        this.sidebarStateCallbacks.push(callback);
      },
    },
    comments: {
      onCommentsPanelSelectThread: () => {},
      registerOnReopenResolvedComment: () => {},
    },
    modals: {
      showFlag: (props: ContentTypesFlagProps) => {
        this.showFlag(props);
      },
      hideFlag: (flagId: string) => {
        this.hideFlag(flagId);
      },
    },
  };

  private notifySidebarStateChange(state: ConfluenceSidebarState) {
    this.sidebarStateCallbacks.forEach((callback) => {
      try {
        callback(state);
      } catch (error) {
        console.error('Error in sidebar state callback:', error);
      }
    });
  }
}

export const contentAPILocal = new ContentAPILocal();
